import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import './Css/PlanCheckTracker.css';
import './Css/PlanCheckTracker-responsive.css';
import { Ripple } from "primereact/ripple";
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import NoDataCreated from "../../../../../Components/NoDataCreated";
import ErrorPage from "../../../../../Components/ErrorPage";
import PageLoader from "../../../../../Components/PageLoader";
import { goToTop } from "../../../../../Components/goToTop";
import { checkIsArchitect, checkIsBuildingInspector } from "../../../../../Utils/LoginUserRoleName";
import { PlanCheckTrackerTemplateList } from "../../../../../Controllers/Types/PlanCheck/PlanCheckTrackerType";
import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";
import { getAllPlanChckTrackerList } from "../../../../../Controllers/Thunks/PlanCheckTrackerThunks";
import { createPlanCheckTracker } from "../../../../../Api/PlanCheck/PlanCheckApiServices";
import { PlanCheckTracker as PlanCheckTrackerType } from "../../../../../Controllers/Types/PlanCheck/PlanCheckTrackerType";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import { PlanCheckDetailReference } from "../../../../../Controllers/Types/PlanCheck/PlanCheckReference";
import { fetchPlanCheckReference } from "../../../../../Controllers/Thunks/PlanCheckThunks";
import CustomTableLoader from "../../../../../Components/CustomTableLoader";
import { AnimatePresence, motion } from "framer-motion";
import { Tooltip } from "primereact/tooltip";
import GoBackButton from "../../../../../Components/GoBackButton";

const PlanCheckTracker: React.FC = () => {
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const dispatch = useAppDispatch();
    const location = useLocation();
    const projectData = location.state?.data;
    const serviceId = Number(projectData.folder_id);

    const loadingPage = useAppSelector((state) => state?.planCheckTrackerList?.loader);
    const error = useAppSelector((state) => state?.planCheckTrackerList?.errorDisplay);

    const [showPlanCheckTrackerOptionsModal, setShowPlanCheckTrackerOptionsModal] = useState<boolean>(false);
    const [basicLoading, setBasicLoading] = useState<boolean>(false);
    const [detailLoading, setDetailLoading] = useState<boolean>(false);

    const [showCustomOptionModal, setShowCustomOptionModal] = useState<boolean>(false);

    const [showTemplatesModal, setShowTemplatesModal] = useState<boolean>(false);
    const [planCheckTrackerTemplatesData, setPlanCheckTrackerTemplatesData] = useState<PlanCheckTrackerTemplateList[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<PlanCheckTrackerTemplateList | null>(null);


    /* Check role */
    const [isArchitect, setIsArchitect] = useState<boolean>(false);
    const [isInspector, setIsInspector] = useState<boolean>(false);

    useEffect(() => {
        const checkRole = async () => {
            const [architectResult, inspectorResult] = await Promise.all([
                checkIsArchitect(),
                checkIsBuildingInspector()
            ]);

            setIsArchitect(architectResult);
            setIsInspector(inspectorResult);
        };

        checkRole();
    }, []);
    /*  */

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const hasPermissionAddPlanCheckTracker = useCanEdit('Plan Check Tracker', 'add_plan_check_tracker');


    useEffect(() => {
        const loadData = async () => {
            const data = await dispatch(getAllPlanChckTrackerList(serviceId));
            const payload = data.payload as PlanCheckTrackerType[];

            if (payload && payload.length > 0) {
                navigate('list', { state: { data: projectData } });
            }
        };

        loadData();
    }, [dispatch, navigate, projectData, serviceId]);

    useEffect(() => {
        const fetchData = async () => {
            setPlanCheckTrackerTemplatesData([])
            // setPlanCheckTrackerTemplatesData(planCheckTemplateLists)
        }
        fetchData();
    }, []);


    /* ****************************** Plan check tracker ****************************** */
    /* Plan check tracker */
    const handleCreatePlanCheckTracker = () => {
        setShowPlanCheckTrackerOptionsModal(true);
    }

    const handleClosePlanCheckTrackerModal = () => {
        setShowPlanCheckTrackerOptionsModal(false);
    }

    const planCheckTrackerOptionModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Select plan check tracker option
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleClosePlanCheckTrackerModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );
    /*  */


    /* For template */
    const handleShowTemplatesModal = () => {
        setShowTemplatesModal(true);
    }

    const handleCloseTemplatesModal = () => {
        setShowTemplatesModal(false);
    }

    const templatesModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Plan check tracker templates
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseTemplatesModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const handleSelectPlanCheckTrackerTemplate = (template: PlanCheckTrackerTemplateList) => {
        setSelectedTemplate(template);
    }
    /*  */


    /* For custom */
    const handleShowCustomOption = () => {
        setShowCustomOptionModal(true);
    }

    const handleCloseCustomOptionModal = () => {
        setShowCustomOptionModal(false);
    }

    const customOptionModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Select custom option type
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseCustomOptionModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const handleCreateCustomPlanCheckTracker = (mode: 'Basic' | 'Detail') => {
        confirmDialog({
            message: `Are you sure you want to create plan check tracker with ${mode} mode?`,
            header: 'Confirmation',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            accept: () => createCustomPlanCheckTracker(mode),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    }

    // database insert functions for create plan check tracker by archetect
    const createCustomPlanCheckTracker = async (mode: 'Basic' | 'Detail') => {
        if (!mode) return;

        mode === 'Basic'
            ? setBasicLoading(true)
            : setDetailLoading(true);

        try {
            const data = await createPlanCheckTracker(serviceId, mode);

            if (data.statusCode === 200) {
                dispatch(getAllPlanChckTrackerList(serviceId));
                handleCloseCustomOptionModal();

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Plan check tracker created successfully.',
                    life: 2000,
                });

                const createdPlanCheckTrackerData = data.data;

                if (createdPlanCheckTrackerData) {
                    if (createdPlanCheckTrackerData.check_type === 'Detail') {
                        navigate(`/project/detail/default-service/pre-commencement/plan-check-tracker/detail`,
                            { state: { data: createdPlanCheckTrackerData, projectData: projectData } }
                        );
                    } else {
                        navigate(`/project/detail/default-service/pre-commencement/plan-check-tracker/basic`,
                            { state: { data: createdPlanCheckTrackerData, projectData: projectData } }
                        );
                    }
                } else {
                    navigate(`list`, { state: { data: projectData } });
                }
                goToTop();
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error creating the plan check tracker.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error creating the plan check tracker:", error);
            toast?.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while creating the plan check tracker.",
                life: 3000,
            });
        } finally {
            mode === 'Basic'
                ? setBasicLoading(false)
                : setDetailLoading(false);
        }
    }
    /*  */
    /* *************************************************************************** */

    /* Plan check tracker samples */
    const [showPlanCheckSampleModal, setShowPlanCheckSampleModal] = useState<boolean>(false);
    const [samplePlanCheckViewState, setSamplePlanCheckViewState] = useState<'Basic' | 'Detail'>('Basic');
    const [activePlanCheckDetail, setActivePlanCheckDetail] = useState<PlanCheckDetailReference | null>(null);

    const { loadingPlanCheckReference, planCheckReferences } = useAppSelector((state) => state?.planCheckList);

    useEffect(() => {
        dispatch(fetchPlanCheckReference());
    }, [dispatch]);

    const handleViewPlanCheckTrackerSample = (mode: 'Basic' | 'Detail') => {
        setSamplePlanCheckViewState(mode);
        setShowPlanCheckSampleModal(true);
        setShowCustomOptionModal(false);
    };

    const handleClosePlanCheckSampleModal = () => {
        setShowPlanCheckSampleModal(false);
        setShowCustomOptionModal(true);
    };

    const planCheckSampleModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">{samplePlanCheckViewState} Plan check tracker</h5>
            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleClosePlanCheckSampleModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const handleToggleDetail = (part: PlanCheckDetailReference) => {
        setActivePlanCheckDetail((prevSelected) =>
            prevSelected?.id === part.id ? null : part
        );
    };
    /*  */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingPage ? (
                    <PageLoader />
                ) : error ? (
                    <ErrorPage errorMessage={error} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                {isInspector && (
                                    <li className="page_breadcrumb_item has_child has_link"
                                        onClick={() => navigate('/project')}
                                    >
                                        Shared folders
                                    </li>
                                )}

                                <li
                                    className={`page_breadcrumb_item has_child ${isArchitect ? 'has_link' : ''}`}
                                    onClick={() => isArchitect ? navigate('/project/detail', { state: { projectData: projectData } }) : null}
                                >
                                    {projectData.job_number} : {projectData.address}
                                </li>

                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>

                                <li className="page_breadcrumb_item active">
                                    {projectData.feature_name}
                                </li>
                            </ul>

                            <GoBackButton />
                        </div>

                        <div className="page_main_content_section p-0">
                            {isArchitect ? (
                                <div className="no_data_created_area with_pointing">
                                    <img src="/assets/images/no_data/no_data_img.png" alt="No Data Found!" />
                                    <h6>
                                        No plan check tracker has been created.
                                        {hasPermissionAddPlanCheckTracker ? 'To create, click the Create button.' : ''}
                                    </h6>

                                    {hasPermissionAddPlanCheckTracker && (
                                        <button className="add_data_link_btn fill radius p-ripple" onClick={handleCreatePlanCheckTracker}>
                                            <i className="bi bi-plus-circle"></i>
                                            <span>Create plan check tracker</span>
                                            <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="no_data_created_area with_pointing">
                                    <img src="/assets/images/no_data/no_data_img.png" alt="No Data Found!" />
                                    <h6>
                                        No plan check tracker was issued by the architect!
                                    </h6>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>


            {/* Plan check tracker option modal */}
            <Dialog
                visible={showPlanCheckTrackerOptionsModal}
                header={planCheckTrackerOptionModalHeader}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_xsm"
                onHide={handleClosePlanCheckTrackerModal}
                dismissableMask
                draggable={false}
            >
                <div className="custom_modal_body">
                    <div className="custom_option_btn_area">
                        <Tooltip target=".info_icon" position="top" />

                        <button
                            className="custom_pc_option_btn p-ripple"
                            onClick={handleShowTemplatesModal}
                            disabled={planCheckTrackerTemplatesData?.length === 0}
                        >
                            <span>
                                <i className="bi bi-ui-checks-grid me-2"></i>
                                Template
                            </span>

                            {planCheckTrackerTemplatesData?.length === 0 && (
                                <i className="bi bi-question-circle info_icon"
                                    data-pr-tooltip="No templates are available now. It will be available soon."
                                ></i>
                            )}

                            <i className="bi bi-chevron-right"></i>
                            <Ripple />
                        </button>

                        <button
                            className="custom_pc_option_btn p-ripple"
                            onClick={handleShowCustomOption}
                        >
                            <span>
                                <i className="bi bi bi-pencil-square me-2"></i>
                                Custom
                            </span>
                            <i className="bi bi-chevron-right"></i>
                            <Ripple />
                        </button>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Custom option modal */}
            <Dialog
                visible={showCustomOptionModal}
                header={customOptionModalHeader}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_md"
                onHide={handleCloseCustomOptionModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="custom_option_area">
                        <div className="row">
                            <div className="col-12 col-sm-6 divide">
                                <div className="custom_option_container">
                                    <h6 className="custom_option_head">
                                        <i className="bi bi-card-checklist me-2"></i>
                                        Basic
                                    </h6>

                                    <p className="custom_option_desc">
                                        Lorem ipsum dolor sit amet consectetur adipisicing elit. Deserunt atque hic
                                    </p>

                                    <div className="custom_option_btn_container">
                                        <Button
                                            label="View sample"
                                            icon="bi bi-eye"
                                            iconPos="left"
                                            onClick={() => handleViewPlanCheckTrackerSample("Basic")}
                                            className="custom_btn sm primary outlined"
                                        />

                                        <Button
                                            label={`${basicLoading ? 'Processing' : 'Create'}`}
                                            icon="bi bi-chevron-right"
                                            iconPos="right"
                                            onClick={() => handleCreateCustomPlanCheckTracker('Basic')}
                                            loading={basicLoading}
                                            className="custom_btn sm primary outlined"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-sm-6">
                                <div className="custom_option_container">
                                    <h6 className="custom_option_head">
                                        <i className="bi bi-card-list me-2"></i>
                                        Detail
                                    </h6>

                                    <p className="custom_option_desc">
                                        Lorem ipsum dolor sit amet consectetur adipisicing elit. Deserunt atque hic excepturi laboriosam qui natus iure modi aperiam in similique?
                                    </p>

                                    <div className="custom_option_btn_container">
                                        <Button
                                            label="View sample"
                                            icon="bi bi-eye"
                                            iconPos="left"
                                            onClick={() => handleViewPlanCheckTrackerSample("Detail")}
                                            className="custom_btn sm primary outlined"
                                        />

                                        <Button
                                            label={`${detailLoading ? 'Processing' : 'Create'}`}
                                            icon="bi bi-chevron-right"
                                            iconPos="right"
                                            onClick={() => handleCreateCustomPlanCheckTracker('Detail')}
                                            loading={detailLoading}
                                            className="custom_btn sm primary outlined"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Template options modal */}
            <Dialog
                visible={showTemplatesModal}
                header={templatesModalHeader}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_lg"
                onHide={handleCloseTemplatesModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="plan_check_templates_area">
                        {planCheckTrackerTemplatesData?.length > 0 ? (
                            <React.Fragment>
                                <h6 className="plan_check_template_head">Choose a template:</h6>
                                <div className="row sow_template_row">
                                    {planCheckTrackerTemplatesData?.map((template) => (
                                        <div key={template.id} className="col-12 col-sm-6 col-lg-6 col-xl-4">
                                            <article className="sow_template p-ripple" style={{ backgroundColor: template.color_code }}
                                                onClick={() => handleSelectPlanCheckTrackerTemplate(template)}>
                                                <div className="temp_icon">
                                                    <img src={template.logo} alt={template.name} />
                                                </div>
                                                <h6>{template.name}</h6>
                                                <Ripple />
                                            </article>
                                        </div>
                                    ))}
                                </div>
                            </React.Fragment>
                        ) : (
                            <NoDataCreated
                                containerClassname="pos_rel"
                                message={`There are no templates for plan check tracker. Try creating one using the custom option.`}
                            />
                        )}
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Plan check tracker sample modal */}
            <Dialog
                visible={showPlanCheckSampleModal}
                header={planCheckSampleModalHeader}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_md"
                onHide={handleClosePlanCheckSampleModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="page_main_content_section mt-0">
                        {loadingPlanCheckReference ? (
                            <CustomTableLoader />
                        ) : (
                            planCheckReferences && (
                                samplePlanCheckViewState === 'Basic' ? (
                                    planCheckReferences?.basic_part?.length > 0 ? (
                                        <div className="pck_track_list_area">
                                            <div className="table-responsive pck_track_list_table_area">
                                                <table className="table pck_track_list_table">
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: "100%" }}>Item detail</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {planCheckReferences?.basic_part.map((item) => (
                                                            <tr key={item?.id} className="part_item_row">
                                                                <td style={{ width: "100%" }}>
                                                                    <div className="item_list_wrapper">
                                                                        <div className="pck_track_list_item_area warning">
                                                                            <div className="pck_track_list_item_header">
                                                                                <span className="item_code">
                                                                                    {item?.item_code ?? '---'}.
                                                                                </span>

                                                                                <span className="item_cmt">
                                                                                    {item?.comment ?? '---'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ) : (
                                        <NoDataCreated
                                            containerClassname="w-100"
                                            message="No basic plan check reference data available."
                                        />
                                    )
                                ) : (
                                    planCheckReferences?.detail_part?.length > 0 ? (
                                        <div className="pck_track_list_area">
                                            <div className="table-responsive pck_track_list_table_area">
                                                <table className="table pck_track_list_table">
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: "4%" }} className="text_no_wrap"></th>

                                                            <th style={{ width: "16%" }} className="text_no_wrap">
                                                                Part no.
                                                            </th>

                                                            <th style={{ width: "70%" }} className="text_no_wrap">
                                                                Part title
                                                            </th>

                                                            <th style={{ width: "10%" }} />
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {planCheckReferences?.detail_part.map((part) => {
                                                            const isActive = part?.id === activePlanCheckDetail?.id;
                                                            return (
                                                                <React.Fragment>
                                                                    <tr
                                                                        key={`part_${part?.id}`}
                                                                        onClick={() => handleToggleDetail(part)}
                                                                        className={`part_row ${isActive ? "active" : ""}`}
                                                                    >
                                                                        <td
                                                                            style={{ width: "3rem" }}
                                                                            className="text-center">
                                                                            <i className={`bi bi-chevron-right ${isActive ? "toggled" : ""} data_toggle_icon`}></i>
                                                                        </td>

                                                                        <td
                                                                            style={{ width: "16%" }}
                                                                            className="text_no_wrap">
                                                                            {part?.part_number}
                                                                        </td>

                                                                        <td
                                                                            style={{ width: "70%" }}
                                                                            className="note_column"
                                                                        >
                                                                            {part?.part_title}
                                                                        </td>

                                                                        <td style={{ width: "10%" }}>
                                                                            <div className="item_count_lable warning h-auto py-3">
                                                                                <span>
                                                                                    {String(part?.item_list?.length).padStart(2, '0')}
                                                                                </span>
                                                                            </div>
                                                                        </td>
                                                                    </tr>

                                                                    <AnimatePresence initial={false}>
                                                                        {isActive && (
                                                                            <tr className="part_item_row">
                                                                                <td
                                                                                    colSpan={4}
                                                                                    style={{ width: "100%" }}
                                                                                    className="p-0">
                                                                                    <motion.div
                                                                                        initial={{ height: 0, opacity: 0 }}
                                                                                        animate={{ height: "auto", opacity: 1 }}
                                                                                        exit={{ height: 0, opacity: 0 }}
                                                                                        transition={{ duration: 0.3, ease: [0.3, 0, 0.2, 1], }}
                                                                                        style={{ overflow: "hidden" }}>
                                                                                        <div className="item_list_wrapper">
                                                                                            {part?.item_list && part?.item_list?.length > 0 ? (
                                                                                                <React.Fragment>
                                                                                                    {part?.item_list.map((item) => (
                                                                                                        <div key={`item_${item?.id}`}
                                                                                                            className="pck_track_list_item_area warning">
                                                                                                            <div className="pck_track_list_item_header ps-4">
                                                                                                                <span className="item_code">
                                                                                                                    {item?.item_code}.
                                                                                                                </span>

                                                                                                                <span className="item_cmt first_letter_capitalized">
                                                                                                                    {item?.comment}
                                                                                                                </span>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    ))}
                                                                                                </React.Fragment>
                                                                                            ) : (
                                                                                                <div className="no_item_area">
                                                                                                    <span>
                                                                                                        {`No item available for ${part?.part_title}.`}
                                                                                                    </span>
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </motion.div>
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                    </AnimatePresence>
                                                                </React.Fragment>
                                                            )
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ) : (
                                        <NoDataCreated
                                            containerClassname="w-100"
                                            message="No detail plan check reference data available."
                                        />
                                    )
                                )
                            )
                        )}
                    </div>
                </div>
            </Dialog>
            {/*  */}
        </React.Fragment>
    )
}

export default PlanCheckTracker;